import { LegalModal } from "@hm/fundamental"
import { LegalDocumentType } from "@shared/api"
import { useAuthApiClient, useLegalClient } from "@shared/api/client"
import { useAuth } from "@shared/lib/auth"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { LegalFlow } from "./LegalFlow"

interface LegalProviderProps {
	product: string
	region: string
	documentTypes: LegalDocumentType[]
	languages: string[]
	children: React.ReactNode
}

export const LegalProvider = ({
	product,
	region,
	documentTypes,
	languages,
	children,
}: LegalProviderProps) => {
	const { t } = useTranslation("app", { keyPrefix: "legal" })

	const { userData } = useAuth()
	const legalClient = useLegalClient()
	const authClient = useAuthApiClient()

	const [open, setOpen] = useState(false)
	const [currentStep, setCurrentStep] = useState(0)
	const [tcContents, setTcContents] = useState<
		{ language: string; content: string; version: string }[]
	>([])
	const [dppContents, setDppContents] = useState<
		{ language: string; content: string; version: string }[]
	>([])

	const legalFlow = useMemo(
		() => new LegalFlow({ user: userData, legalClient, authClient }),
		[userData, legalClient, authClient],
	)

	const scrollModalUp = () =>
		document.querySelector('[data-cy="legal-modal"]')?.scrollTo({
			top: 0,
			behavior: "smooth",
		})

	const checkAcceptedLegalDocuments = async () => {
		if (!legalFlow.user) {
			return
		}

		const actualVersions = await legalFlow.fetchActualLegalVersions(
			product,
			documentTypes,
			region,
		)

		const tcVersion = actualVersions.find(
			({ docType }) => docType === "tc",
		)?.version
		const needsToAcceptTc = tcVersion
			? !legalFlow.hasAcceptedLatestTerms(tcVersion)
			: false

		const dppVersion = actualVersions.find(
			({ docType }) => docType === "dpp",
		)?.version
		const needsToAcceptDpp = dppVersion
			? !legalFlow.hasAcceptedLatestTermsForDpp(dppVersion)
			: false

		if (needsToAcceptTc || needsToAcceptDpp) {
			const tcContents = await legalFlow.loadLegalDocuments(
				product,
				"tc",
				region,
				languages,
				tcVersion!.version,
			)
			const dppContents = await legalFlow.loadLegalDocuments(
				product,
				"dpp",
				region,
				languages,
				dppVersion!.version,
			)
			setTcContents(tcContents)
			setDppContents(dppContents)
			setOpen(true)
			setCurrentStep(needsToAcceptTc ? 0 : 1)
		}
	}

	useEffect(() => {
		checkAcceptedLegalDocuments()
	}, [userData])

	return (
		<>
			{open ? (
				<LegalModal
					data-cy="legal"
					open={open}
					currentStep={currentStep}
					steps={[
						{
							title: t("termsAndConditions.title"),
							label: "1",
							content: (
								<div
									dangerouslySetInnerHTML={{
										__html: tcContents[0].content,
									}}
								/>
							),
							submitTitle: t("termsAndConditions.submitTitle"),
							onSubmit: async () => {
								await legalFlow.acceptTerms(
									tcContents[0].version,
								)
								setCurrentStep(currentStep + 1)
								scrollModalUp()
							},
						},
						{
							title: t("privacyPolicy.title"),
							label: "2",
							content: (
								<div
									dangerouslySetInnerHTML={{
										__html: dppContents[0].content,
									}}
								/>
							),
							backTitle: t("privacyPolicy.backTitle"),
							submitTitle: t("privacyPolicy.submitTitle"),
							onBack: () => {
								setCurrentStep(currentStep - 1)
								scrollModalUp()
							},
							onSubmit: async () => {
								await legalFlow.acceptDpp(
									dppContents[0].version,
								)
								setOpen(false)
							},
						},
					]}
					onClose={() => {}}
				/>
			) : (
				<>{children}</>
			)}
		</>
	)
}
